import { useActiveItemIndexStore } from '@fukku/slideshow/hooks/useActiveItemIndexStore/useActiveItemIndexStore'
import { useSlideshowContext } from '@fukku/slideshow/hooks/useSlideshowContext/useSlideshowContext'
import {
	SlideshowFromDirection,
	type UseSlideshowLoop,
} from '@fukku/slideshow/types/Slideshow'
import { useCallback, useMemo } from 'react'

const SECOND_LAST_INDEX = 2

export function useSlideshowLoop(): UseSlideshowLoop {
	const { slideshowRef, nItems, updateStyles } = useSlideshowContext()

	const { setIndex: setActiveItemIndex } = useActiveItemIndexStore()

	// Utility function to force a reflow of the slideshow element
	const forceReflow = useCallback(() => {
		// eslint-disable-next-line @typescript-eslint/no-unused-expressions
		void slideshowRef.current?.offsetWidth
	}, [slideshowRef])

	const loopItems = useCallback(
		(toPreviousSlide: boolean): void => {
			const parentElement = slideshowRef.current

			if (!parentElement) {
				return
			}

			if (toPreviousSlide) {
				// Move last element to first position
				const lastChild = parentElement.lastElementChild
				if (lastChild) {
					parentElement.insertBefore(lastChild, parentElement.firstElementChild)
				}
			} else {
				// Move first element to last position
				const firstChild = parentElement.firstElementChild

				if (firstChild) {
					parentElement.appendChild(firstChild)
				}
			}
		},
		[slideshowRef]
	)

	const handleLoop = useCallback(
		(fromDirection: SlideshowFromDirection, preserveIndex = false): void => {
			const toPreviousSlide = fromDirection === SlideshowFromDirection.Left

			// If the loop is for the previous item, we set the active item to 1 (previously 0)
			// If the loop is for the next item, we set the active item to length - 2 (previously length -1)
			const nextIndex = toPreviousSlide ? 1 : nItems - SECOND_LAST_INDEX

			loopItems(toPreviousSlide)
			updateStyles({ activeItemIndex: nextIndex })

			// This is used to fix flickering issues in Firefox when sliding
			forceReflow()

			// We have to delay this in order to let the previous loop styles effect make effect and be processed by the browser
			setTimeout(() => {
				const indexOffset = toPreviousSlide ? -1 : 1
				setActiveItemIndex(nextIndex + (preserveIndex ? indexOffset : 0))
			})
		},
		[loopItems, nItems, setActiveItemIndex, updateStyles, forceReflow]
	)

	return useMemo(() => ({ handleLoop }), [handleLoop])
}
